import {ApolloLink} from '@apollo/client';
import {Kind, OperationTypeNode} from 'graphql';
import type {ApolloCache} from '@apollo/client';
import type {DocumentNode} from 'graphql';

// Stamped valuation fields on the unified Transaction type. They go stale on
// entities still waiting for basis (auto-assignment can value those the
// moment a conversion lands) and on valued FOREIGN entities (attaching
// another raw restamps the target's draws). Only USD stamps never move.
const TRANSACTION_FIELDS = ['homeNetAmount', 'needsBasis', 'basisDraws'];

/**
 * Evict every cached foreign row's basis-derived fields.
 *
 * These scalars are computed server-side from CURRENT basis-lot state, so
 * they go stale whenever capacity changes — and capacity can change through
 * almost any transaction mutation: creating over foreign rows, splitting,
 * attaching to an existing transaction, assigning basis, resetting a
 * transaction or raw (including cascades from a conversion's USD leg),
 * merging, editing an undrawn lot's date (its FIFO position) — and a
 * conversion recorded AFTER its spending now auto-assigns that spending's
 * homeNetAmount server-side. Successive reviews of #279 each found another
 * missed call site, so this is deliberately NOT wired per call site — the
 * link below runs it after every mutation. Over-invalidation is cheap: only
 * these fields on non-USD entities are evicted (foreign rows are rare, and
 * all-USD lists keep their accumulated cursor pages and scroll restoration
 * untouched); a query containing an affected entity becomes incomplete and
 * refetches on its next render or cache-first mount.
 */
export const invalidateHomeAmountPreviews = (
  cache: ApolloCache<object>,
): void => {
  const entities = cache.extract();
  let evicted = false;
  for (const [id, entity] of Object.entries(entities)) {
    if (entity == null) continue;
    if (entity.__typename === 'RawTransaction') {
      // Foreign rows only: a USD row's amount IS its dollar value and
      // carries no preview, so all-USD lists stay complete and keep their
      // cursor pages and scroll restoration.
      const currency = entity.currency;
      if (typeof currency !== 'string' || currency === 'USD') continue;
      if (!('homeAmountPreview' in entity)) continue;
      cache.evict({id, fieldName: 'homeAmountPreview'});
      evicted = true;
      continue;
    }
    if (entity.__typename === 'Transaction') {
      // Two arms, both needed. needsBasis === true catches transactions a
      // conversion can value behind the cache's back — including pending
      // conversions themselves, which are stored with currency USD (even
      // foreign-to-foreign ones) and would slip through a currency filter.
      // currency !== USD catches VALUED foreign transactions: attaching
      // another foreign raw to one ("Add to transaction") restamps its
      // draws server-side, so its stamp is not immutable to every mutation
      // — only USD transactions' values truly never move.
      if (
        entity.needsBasis !== true &&
        (typeof entity.currency !== 'string' || entity.currency === 'USD')
      )
        continue;
      for (const fieldName of TRANSACTION_FIELDS) {
        if (!(fieldName in entity)) continue;
        cache.evict({id, fieldName});
        evicted = true;
      }
    }
  }
  if (evicted) cache.gc();
};

export const isMutation = (query: DocumentNode): boolean =>
  query.definitions.some(
    (definition) =>
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.MUTATION,
  );

/**
 * Apollo link that invalidates cached previews after every mutation —
 * complete by construction, where per-call-site invalidation kept missing
 * lot-changing paths. Runs before the mutation's own result is written to
 * the cache, which is fine: it only evicts a field on pre-existing rows,
 * and rows returned by the mutation arrive with fresh previews.
 */
export const createPreviewInvalidationLink = (
  cache: ApolloCache<object>,
): ApolloLink =>
  new ApolloLink((operation, forward) =>
    forward(operation).map((result) => {
      if (isMutation(operation.query)) {
        invalidateHomeAmountPreviews(cache);
      }
      return result;
    }),
  );
